import React, { useContext, useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import { ROUTER_BETTER_AUTH, ROUTER_DIAGRAMS, ROUTER_FAVORITE } from '@/entry/route';
import { EasyDrawContext } from '@/store';
import classNames from 'classnames';
import GoAuth from './GoAuth';

import './styles.less';

type TabKey = 'overview' | 'favorite';

type TabItem = {
  key: TabKey;
  label: string;
  path: string;
};

const prefixCls = 'canvopix-project-layout';

export default observer(function CanvopixProjectLayout() {
  const { t } = useTranslation('app', { keyPrefix: 'pages' });
  const location = useLocation();
  const navigate = useNavigate();
  const store = useContext(EasyDrawContext);

  const tabs = useMemo<TabItem[]>(
    () => [
      { key: 'overview', label: t('welcome.nav.my_space'), path: ROUTER_DIAGRAMS },
      { key: 'favorite', label: t('project.my_favorite'), path: ROUTER_FAVORITE },
    ],
    [t],
  );

  const activeKey: TabKey = location.pathname.startsWith(ROUTER_FAVORITE) ? 'favorite' : 'overview';

  if (!store.isLogin) {
    const callbackURL = `${location.pathname}${location.search}${location.hash}`;
    return (
      <div className={classNames(prefixCls, '-mx-3 -my-3 sm:-mx-4 sm:-my-4 text-[var(--cp-fg)]')}>
        <GoAuth
          onLogin={() =>
            navigate(`${ROUTER_BETTER_AUTH}?callbackURL=${encodeURIComponent(callbackURL)}`)
          }
        />
      </div>
    );
  }

  return (
    <div className={classNames(prefixCls, '-mx-3 -my-3 sm:-mx-4 sm:-my-4 text-[var(--cp-fg)]')}>
      <div className="min-h-[calc(100vh-var(--cp-header-h))] bg-[radial-gradient(900px_520px_at_12%_-10%,rgb(var(--cp-fg-rgb)/0.06)_0%,rgb(var(--cp-fg-rgb)/0)_58%),radial-gradient(780px_460px_at_92%_8%,rgb(var(--cp-fg-rgb)/0.04)_0%,rgb(var(--cp-fg-rgb)/0)_55%),var(--cp-bg)]">
        <div className="mx-auto w-full max-w-[1200px] px-4 py-5 sm:px-6 sm:py-6 lg:px-8">
          <div className="sticky z-30 mb-5 flex items-center justify-start sm:mb-6" style={{ top: 'calc(var(--cp-header-h) + 12px)' }}>
            <div className="w-full max-w-[520px] bg-[var(--cp-nav-bg)] backdrop-blur">
              <div className="flex items-center gap-6 px-1">
                {tabs.map((tab) => {
                  const isActive = activeKey === tab.key;
                  return (
                    <button
                      key={tab.key}
                      type="button"
                      onClick={() => navigate(tab.path)}
                      className={classNames(
                        'appearance-none border-0 bg-transparent',
                        'group relative select-none rounded-md px-1 py-3 text-sm font-semibold tracking-tight transition-colors duration-150 hover:bg-[rgb(var(--cp-fg-rgb)/0.04)]',
                        'focus:outline-none',
                        isActive
                          ? 'text-[var(--cp-fg)]'
                          : 'text-[var(--cp-muted)] hover:text-[var(--cp-fg)]',
                      )}
                    >
                      {tab.label}
                      <span
                        className={classNames(
                          'pointer-events-none absolute -bottom-[1px] left-0 right-0 h-[2px] rounded-full transition-opacity duration-150',
                          isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-40',
                        )}
                        style={{ backgroundColor: 'rgb(var(--cp-fg-rgb) / 0.78)' }}
                      />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="min-w-0">
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
});
